import { Fragment, PureComponent, ReactNode } from 'react';
import {
    components,
    OptionProps,
    ValueContainerProps,
    ControlProps,
    MenuProps,
    StylesConfig,
} from 'react-select';
import AsyncSelect from 'react-select/async';
import Fuse from 'fuse.js';
import { CSSObject } from '@emotion/serialize';

import { ONE_CHAR_GENDER } from '../pages/NewPatient';

import { getAllPatients } from '../store/idbHandler';
import { Patient } from '../store/types/patient';
import { Personal } from '../store/types/patient';
import { AntTooltip } from './common/AntTooltip';
import { AbhaIconNotLinked, EkaCareIcon } from './common/Icons';

interface PersonalWithUhid extends Personal {
    uhid: string;
}

type SuggestionItem = Fuse.FuseResult<Patient>;

const fuseConfig = {
    keys: ['personal.uhid'],
    threshold: 0.2,
};

const Option = (props: OptionProps<SuggestionItem, false>) => {
    const { data, selectProps } = props;
    const { isPhCode } = selectProps;

    const ptname = [data.item.personal.name.f, data.item.personal.name.l].filter(Boolean).join(' ');

    return (
        <components.Option {...props}>
            <div className="flex items-center justify-between font-600 text-neutral-3">
                <div className="flex w-full space-x-12">
                    <div className="flex items-center flex-1">
                        {data.item.personal.onApp && (
                            <AntTooltip content="Connected on eka.care app">
                                <span className="top-1 right-1 relative mr-3 w-16 h-16">
                                    <EkaCareIcon />
                                </span>
                            </AntTooltip>
                        )}
                        {data?.item?.abha?.id && (
                            <AntTooltip content="ABHA address is present">
                                {AbhaIconNotLinked}
                            </AntTooltip>
                        )}
                        <p>
                            {`${data.item.personal.uhid + ', ' || ''}`}
                            {ptname}
                            {`, ${
                                isPhCode && data.item.personal.phone?.c
                                    ? `${data.item.personal.phone?.c} `
                                    : ''
                            }${data.item.personal.phone?.n || ''}`}
                        </p>
                    </div>
                    <div>
                        {ONE_CHAR_GENDER[data.item.personal.gender.toLowerCase()]}
                        {data.item.personal.age?.value
                            ? `, ${data.item.personal.age?.value} Y`
                            : ''}
                    </div>
                </div>
            </div>
        </components.Option>
    );
};

const ValueContainer = (props: ValueContainerProps<SuggestionItem, false>) => {
    const { hasValue, children, getValue, setValue, selectProps } = props;
    const { isModal } = selectProps;
    if (hasValue) {
        const value: Patient = getValue()[0].item;
        return isModal ? (
            <div className="relative flex items-center justify-between gap-8 bg-darwin-neutral-50 py-10 px-12">
                <div className="flex items-center justify-center w-16 h-16 icon">
                    <svg
                        width="13"
                        height="13"
                        viewBox="0 0 13 13"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M11.9688 3.46875C12.3242 3.46875 12.625 3.76953 12.625 4.15234C12.625 4.50781 12.3242 4.78125 11.9688 4.78125L9.69922 4.80859L8.98828 8.74609H11.0938C11.4492 8.74609 11.75 9.04688 11.75 9.40234C11.75 9.75781 11.4492 10.0312 11.0938 10.0312H8.74219L8.33203 12.3555C8.27734 12.6562 8.00391 12.875 7.70312 12.875C7.64844 12.875 7.62109 12.875 7.56641 12.875C7.21094 12.8203 6.96484 12.4648 7.04688 12.1094L7.40234 10.0312H4.36719L3.95703 12.3555C3.90234 12.6562 3.62891 12.875 3.32812 12.875C3.27344 12.875 3.24609 12.875 3.19141 12.875C2.83594 12.8203 2.61719 12.4648 2.67188 12.1094L3.05469 10.0312H1.03125C0.648438 10.0312 0.375 9.75781 0.375 9.375C0.375 9.01953 0.648438 8.71875 1.03125 8.71875H3.27344L3.98438 4.78125H1.90625C1.52344 4.78125 1.25 4.50781 1.25 4.15234C1.25 3.76953 1.52344 3.46875 1.90625 3.46875H4.23047L4.64062 1.17188C4.69531 0.816406 5.05078 0.597656 5.40625 0.652344C5.76172 0.707031 5.98047 1.0625 5.92578 1.41797L5.57031 3.46875H8.60547L9.01562 1.17188C9.07031 0.816406 9.42578 0.597656 9.78125 0.652344C10.1367 0.707031 10.3555 1.0625 10.3008 1.41797L9.91797 3.46875H11.9688ZM7.64844 8.71875L8.35938 4.78125H5.32422L4.61328 8.71875H7.64844Z"
                            fill="#3E445C"
                        />
                    </svg>
                </div>
                <input
                    type="text"
                    value={value.formData?.find((i) => i.key === 'uhid')?.value || ''}
                    onChange={() => {
                        setValue(null, 'deselect-option');
                    }}
                    className="focus:outline-none text-14 font-400 placeholder-neutral-6 w-full bg-darwin-neutral-50"
                />
                <div className="mimic-box"></div>
            </div>
        ) : (
            <div className="add-pt-input">
                <input
                    type="text"
                    value={value.formData?.find((i) => i.key === 'uhid')?.value || ''}
                    onChange={() => {
                        setValue(null, 'deselect-option');
                    }}
                    className="focus:outline-none text-14 font-700 placeholder-neutral-6 w-full"
                />
                <div className="flex items-center justify-center w-16 h-16 icon">
                    <svg
                        className="w-16 h-16 fill-neutral-5"
                        role="img"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 448 512"
                    >
                        <path d="M272 304h-96C78.8 304 0 382.8 0 480c0 17.67 14.33 32 32 32h384c17.67 0 32-14.33 32-32C448 382.8 369.2 304 272 304zM48.99 464C56.89 400.9 110.8 352 176 352h96c65.16 0 119.1 48.95 127 112H48.99zM224 256c70.69 0 128-57.31 128-128c0-70.69-57.31-128-128-128S96 57.31 96 128C96 198.7 153.3 256 224 256zM224 48c44.11 0 80 35.89 80 80c0 44.11-35.89 80-80 80S144 172.1 144 128C144 83.89 179.9 48 224 48z"></path>
                    </svg>
                </div>
                <div className="mimic-box"></div>
            </div>
        );
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const child = children[children.length - 1];

    return child;
};

const Control = (props: ControlProps<SuggestionItem, false>) => {
    const { children } = props;
    return <Fragment>{children}</Fragment>;
};

const Menu = (props: MenuProps<SuggestionItem, false>) => {
    const { children, options } = props;
    if (options.length === 0) {
        return null;
    }
    return <components.Menu {...props}>{children}</components.Menu>;
};

// TODO
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const Input = (props) => {
    const { selectProps, onChange, onFocus, value } = props;
    const { placeholder, onUhidBlur, isModal } = selectProps;

    const onCChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        onChange(e);
    };

    return isModal ? (
        <div
            className={
                'relative flex items-center justify-between gap-8 bg-darwin-neutral-50 py-10 px-12'
            }
        >
            <div className="flex items-center justify-center w-16 h-16 icon">
                <svg
                    width="13"
                    height="13"
                    viewBox="0 0 13 13"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M11.9688 3.46875C12.3242 3.46875 12.625 3.76953 12.625 4.15234C12.625 4.50781 12.3242 4.78125 11.9688 4.78125L9.69922 4.80859L8.98828 8.74609H11.0938C11.4492 8.74609 11.75 9.04688 11.75 9.40234C11.75 9.75781 11.4492 10.0312 11.0938 10.0312H8.74219L8.33203 12.3555C8.27734 12.6562 8.00391 12.875 7.70312 12.875C7.64844 12.875 7.62109 12.875 7.56641 12.875C7.21094 12.8203 6.96484 12.4648 7.04688 12.1094L7.40234 10.0312H4.36719L3.95703 12.3555C3.90234 12.6562 3.62891 12.875 3.32812 12.875C3.27344 12.875 3.24609 12.875 3.19141 12.875C2.83594 12.8203 2.61719 12.4648 2.67188 12.1094L3.05469 10.0312H1.03125C0.648438 10.0312 0.375 9.75781 0.375 9.375C0.375 9.01953 0.648438 8.71875 1.03125 8.71875H3.27344L3.98438 4.78125H1.90625C1.52344 4.78125 1.25 4.50781 1.25 4.15234C1.25 3.76953 1.52344 3.46875 1.90625 3.46875H4.23047L4.64062 1.17188C4.69531 0.816406 5.05078 0.597656 5.40625 0.652344C5.76172 0.707031 5.98047 1.0625 5.92578 1.41797L5.57031 3.46875H8.60547L9.01562 1.17188C9.07031 0.816406 9.42578 0.597656 9.78125 0.652344C10.1367 0.707031 10.3555 1.0625 10.3008 1.41797L9.91797 3.46875H11.9688ZM7.64844 8.71875L8.35938 4.78125H5.32422L4.61328 8.71875H7.64844Z"
                        fill="#3E445C"
                    />
                </svg>
            </div>

            <input
                type="text"
                pattern="\d+"
                placeholder={placeholder}
                className="focus:outline-none text-14 font-400 placeholder-neutral-6 w-full bg-darwin-neutral-50"
                value={value}
                onChange={onCChange}
                onFocus={onFocus}
                onBlur={onUhidBlur}
            />
            <div className="mimic-box"></div>
        </div>
    ) : (
        <div className={'add-pt-input'}>
            <input
                type="text"
                pattern="\d+"
                placeholder={placeholder}
                className="focus:outline-none text-14 font-700 placeholder-neutral-6 w-full"
                value={value}
                onChange={onCChange}
                onFocus={onFocus}
                onBlur={onUhidBlur}
            />
            <div className="mimic-box"></div>
        </div>
    );
};

const DropdownIndicator = () => null;
const NoOptionsMessage = () => null;
const LoadingIndicator = () => null;
const ClearIndicator = () => null;
const IndicatorsContainer = () => null;

const customComponents = {
    DropdownIndicator,
    ValueContainer,
    Input,
    Control,
    Option,
    NoOptionsMessage,
    LoadingIndicator,
    Menu,
    ClearIndicator,
    IndicatorsContainer,
};

const customStyles: StylesConfig<SuggestionItem, false> = {
    menu: (base) => {
        return {
            ...base,
            paddingTop: 0,
        } as CSSObject;
    },
};

type Props = {
    inputValue: string;
    onSelect: (p?: Patient) => void;
    onInputChange: (text: string) => void;
    suggestionsEnabled: boolean;
    isModal?: boolean;
    isPhCode: boolean | undefined;
};

type State = {
    suggestedPatients: Patient[];
    menuIsOpen: boolean;
};

class PatientUhidAutoSuggest extends PureComponent<Props, State> {
    patientsFuse = new Fuse([] as Patient[], fuseConfig);

    state: State = {
        suggestedPatients: [],
        menuIsOpen: false,
    };

    constructor(props: Props) {
        super(props);

        getAllPatients()
            .then((res) => {
                const allPt = res?.filter((t) => !t.archived) || [];
                allPt.forEach((i) => {
                    (i.personal as PersonalWithUhid).uhid =
                        '' + (i.formData?.find((i) => i.key === 'uhid')?.value || '');
                });
                this.patientsFuse = new Fuse(allPt, fuseConfig);
            })
            .catch(console.error);
    }

    onUhidChange = (input: string): void => {
        this.setState({
            menuIsOpen: true,
        });

        this.props.onInputChange(input);
    };

    onChange = (patient: SuggestionItem | null): void => {
        const { onSelect } = this.props;

        if (patient === null) {
            onSelect();
            return;
        }

        onSelect(patient.item);
    };

    onUhidBlur = (): void => {
        this.setState({ menuIsOpen: false });
    };

    loadPatients = async (text: string): Promise<SuggestionItem[]> => {
        if (!this.props.suggestionsEnabled) {
            return [];
        }

        const allPatients: SuggestionItem[] = this.patientsFuse.search(text).slice(0, 4);
        return allPatients;
    };

    private readonly filterOption = () => true;

    render(): ReactNode {
        const { menuIsOpen } = this.state;
        const { inputValue } = this.props;

        const children = (
            <AsyncSelect
                value={[]}
                isClearable={true}
                components={customComponents}
                styles={customStyles}
                loadOptions={this.loadPatients}
                onInputChange={this.onUhidChange}
                inputValue={inputValue}
                onChange={this.onChange}
                onUhidBlur={this.onUhidBlur}
                filterOption={this.filterOption}
                menuIsOpen={menuIsOpen}
                tabSelectsValue={false}
                placeholder={this.props.isModal ? 'Enter UHID' : 'UHID'}
                className={this.props.isModal ? 'bg-darwin-neutral-50 w-full' : ''}
                isModal={this.props.isModal ?? false}
                isPhCode={this.props.isPhCode}
            />
        );

        return this.props.isModal ? (
            <div className="space-y-4 flex justify-between flex-grow">{children}</div>
        ) : (
            children
        );
    }
}

export default PatientUhidAutoSuggest;
